import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { AlertService } from '@app/core';
import { AssessmentStatusAttemptEnum, GenericModel } from '@app/core/models';
import { notNull } from '@app/shared';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { first, isEqual, isNil, orderBy, uniqBy } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ModalAuditActions, ModalAuditSelectors } from '../../store';
import { AttemptStudentData, ModalAuditState } from '../../store/modal-audit.state';
import { getAttemptStatus } from '../../utils';

@Component({
  selector: 'app-sidenav-students',
  templateUrl: './sidenav-students.component.html',
})
export class SidenavStudentsComponent {
  public selectPendingCtrl = new FormControl(false);
  public groupCtrl = new FormControl(undefined);
  public unasignedCtrl = new FormControl(undefined);
  public groups$ = new BehaviorSubject<GenericModel[]>(undefined);
  public studentsFiltered$ = new BehaviorSubject<AttemptStudentData[]>(undefined);
  public studentsWithoutAttempt$: Observable<AttemptStudentData[]>;
  private _students$ = new BehaviorSubject<AttemptStudentData[]>(undefined);
  private _studentActive: AttemptStudentData;

  constructor(
    private _store: Store<ModalAuditState>,
    private _destroyRef: DestroyRef,
    private _translateService: TranslateService,
    private _alertService: AlertService,
  ) {
    combineLatest({
      students: this._students$?.pipe(takeUntilDestroyed(this._destroyRef), notNull()),
      groupId: this.groupCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef), startWith(undefined)),
      pending: this.selectPendingCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef), startWith(false)),
    })
      .pipe(debounceTime(100))
      .subscribe(this.filterStudents);

    this._store
      .select(ModalAuditSelectors.selectStudentActive)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(isEqual),
        tap((student) => {
          if (!this._students$.value?.length) {
            this.groupCtrl.setValue(student.turma_descricao);
          }
        }),
      )
      .subscribe((student) => (this._studentActive = student));

    this._store
      .select(ModalAuditSelectors.selectStudents)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(isEqual),
        filter((students) => !!students?.length),
        tap(this.initGroups),
        tap((students) => {
          if (!students.some((student) => student.selected)) {
            this.onSetSelectedUser(first(students));
          }
        }),
      )
      .subscribe((students) => this._students$.next(students));

    this.groupCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        startWith(undefined),
        distinctUntilChanged(isEqual),
        pairwise(),
        filter(([valuePrevious]) => !!valuePrevious),
      )
      .subscribe(([valuePrevious, value]) => {
        if (valuePrevious !== value) {
          const student = first(this._students$.value.filter((student) => student.turma_descricao === value));
          this.onSetSelectedUser(student);
        }
      });

    this.studentsWithoutAttempt$ = this.studentsFiltered$.pipe(
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      map((students) => students.filter((student) => !!student.usuario_id && !student.tentativa_uuid)),
    );

    this.unasignedCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        notNull(),
        switchMap((student) =>
          combineLatest({
            student: of(student),
            result: this._alertService.confirm({
              title: 'geral.atencao',
              message: 'gestor-avaliacoes.vincular-questao',
            }),
          }),
        ),
        filter(({ result }) => !!result),
      )
      .subscribe(({ student }) => {
        this.unasignedCtrl.reset(undefined, { emitEvent: false });
        this._store.dispatch(
          ModalAuditActions.StudentLoadingReducer({
            attempt_uuid: this._studentActive.tentativa_uuid,
            user_id: student.usuario_id,
            loading: true,
          }),
        );
        this._store.dispatch(
          ModalAuditActions.AssingStudentEffect({ attempt_uuid: this._studentActive.tentativa_uuid, student }),
        );
      });
  }

  public onSetSelectedUser(student: AttemptStudentData) {
    if (!!student.selected) {
      return;
    }
    this._store.dispatch(
      ModalAuditActions.UpdateStudentActiveReducer({
        user_id: student?.usuario_id,
        attempt_uuid: student?.tentativa_uuid,
      }),
    );
  }

  public onGetStatusLabel(student: AttemptStudentData) {
    return getAttemptStatus(student);
  }

  public onFormatSpecialNeeds(necessidadesEspeciais: any[]): string {
    return necessidadesEspeciais.map((necessidade) => necessidade.descricao).join('\n');
  }

  public onRemoveStudent(student) {
    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'gestor-avaliacoes.desvincular-aluno-resposta',
      })
      .pipe(take(1), notNull())
      .subscribe(() => {
        this._store.dispatch(
          ModalAuditActions.StudentLoadingReducer({
            attempt_uuid: student.tentativa_uuid,
            user_id: student.usuario_id,
            loading: true,
          }),
        );
        this._store.dispatch(
          ModalAuditActions.AssingStudentEffect({ attempt_uuid: student.tentativa_uuid, student: undefined }),
        );
      });
  }

  public onRemoveAttempt(student) {
    this._alertService
      .confirm({
        title: 'geral.atencao',
        message: 'gestor-avaliacoes.excluir-tentativa',
      })
      .pipe(take(1), notNull())
      .subscribe(() => {
        this._store.dispatch(
          ModalAuditActions.StudentLoadingReducer({
            attempt_uuid: student.tentativa_uuid,
            user_id: student.usuario_id,
            loading: true,
          }),
        );
        this._store.dispatch(ModalAuditActions.RemoveAttemptEffect({ attempt_uuid: student.tentativa_uuid }));
      });
  }

  public onCheckIsOnline(student: AttemptStudentData): boolean {
    return !isNil(student.is_answer_printed) && !student.is_answer_printed;
  }

  private filterStudents = ({ students, groupId, pending }) => {
    students = students?.filter((student) => !student.usuario_id || student.turma_descricao === groupId);

    if (!!pending) {
      students = students.filter(
        (student) =>
          [
            AssessmentStatusAttemptEnum.ModelUndefined,
            AssessmentStatusAttemptEnum.ResponseCardInvalid,
            AssessmentStatusAttemptEnum.ResponseCardDivergent,
            AssessmentStatusAttemptEnum.Review,
            AssessmentStatusAttemptEnum.Wait,
          ].includes(student.status) || !student.usuario_id,
      );
    }

    students = orderBy(students, [(student) => !student.nome, (student) => student.nome], ['desc', 'asc']);

    this.studentsFiltered$.next(students);
  };

  private initGroups = (students: any[]) => {
    this.groups$.next(
      uniqBy(
        students.map((student) => ({
          id: student.turma_descricao,
          description:
            student.turma_descricao || this._translateService.instant('gestor-avaliacoes.alunos-especificos'),
        })),
        'id',
      ),
    );
  };
}
